import React, { useCallback, useEffect, useState } from "react";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { useInfiniteQuery } from "@tanstack/react-query";
import useAxios from "@libs/axios/useAxios";
import { SEARCH_SELLER_URL, SELLER_URL } from "@libs/apiUrl";
import { LOAD_SIZE_10 } from "@libs/reactQuery/queryClient";
import useIntersection from "@hook/useIntersection";
import Image from "@components/item/Image";
import SearchInput from "@components/input/SearchInput";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SellerCard from "@components/card/SellerCard";
import useUserInfoStore from "@store/useUserInfoStore";

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "2.8rem",
  padding: "0 2rem",
});

const TitleBox = styled.div({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

const Title = styled.h3(({ theme }) => ({
  color: theme.color.black,
  fontSize: "2rem",
  fontWeight: 600,
  margin: 0,
  lineHeight: "2rem",
  display: "flex",
  justifyContent: "space-between",
}));

const BackButton = styled.button({
  display: "flex",
});

const InputWrapper = styled.div({
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: "2rem",
  padding: "2rem 0",
});

const BrandList = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "2rem",
});

const SearchSeller = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState("");
  const [searchParams] = useSearchParams();
  const searchParam = searchParams.get("query");
  const { userInfo } = useUserInfoStore();
  const { t } = useTranslation();

  useEffect(() => {
    if (searchParam) {
      setInputValue(searchParam);
    }
  }, []);

  const { axiosInstance } = useAxios();

  const getSearchSeller = useCallback(
    async ({ pageParam = 1 }) => {
      let url = `${SELLER_URL}?page=${pageParam}&limit=${LOAD_SIZE_10}&nickname=${inputValue}`;
      const res = await axiosInstance.get(url);
      if (res.status === 200) {
        return res.data;
      }
    },
    [axiosInstance, inputValue],
  );

  const { data: sellerList, fetchNextPage } = useInfiniteQuery({
    queryKey: ["sellerList", inputValue],
    queryFn: ({ pageParam = 1 }) => getSearchSeller({ pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPages, pages) =>
      pages?.length < lastPages.data.total_count / LOAD_SIZE_10
        ? pages.length + 1
        : undefined,
    enabled: !!inputValue,
  });

  const { setObserverTarget } = useIntersection(() => {
    fetchNextPage();
  });

  const handleBack = () => {
    navigate(-1);
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const encodedQuery = encodeURIComponent(inputValue.trim());

    if (e.key === "Enter") {
      if (encodedQuery) {
        navigate(`/search/seller?nickname=${encodedQuery}`);
      } else {
        navigate(`/search/seller`);
      }
    }
  };

  return (
    <Container>
      <InputWrapper>
        <BackButton onClick={handleBack}>
          <Image src={theme.image.arrow_left.default} />
        </BackButton>
        <SearchInput
          placeholder="아이템, 셀러 브랜드 명, 키워드 검색"
          inputValue={inputValue}
          setInputValue={setInputValue}
          onClick={handleKeyUp}
        />
      </InputWrapper>

      <TitleBox>
        <Title>{t("셀러")}</Title>
      </TitleBox>

      <BrandList>
        {sellerList?.pages?.map((page) => {
          return page?.data?.list?.map((item: any) => (
            <SellerCard
              id={item.seller_id}
              name={item.nickname}
              follow={item.is_followed === 1}
              src={item.profile_image}
              is_me={item.seller_id === userInfo?.id}
              withImage
              imageList={item.product_image_list}
            />
          ));
        })}
      </BrandList>

      <div ref={setObserverTarget} />
    </Container>
  );
};

export default SearchSeller;
